import { shape, array, bool, string } from 'prop-types'
import { graphql } from 'gatsby'
import { css } from '@emotion/react'
import { Button } from '@livechat/design-system-react-components'
import { useMedia } from 'use-media'

import { LocalImage } from '@/components/LocalImage'
import SEO from '@/components/SEO'
import Card from '@/components/HomePage/Card'
import AppName from '@/components/HomePage/Campaigns/Campaign/AppName'
import AppImage from '@/components/HomePage/Campaigns/Campaign/AppImage'
import { Row, Col, Container } from '@/components/Layout'
import AppDescription from '@/components/HomePage/Campaigns/Campaign/AppDescription'
import mediaQueries from '@/layout/media-queries'
import Layout from '@/layout'

const numberOfDealsInRow = 3

const cardCss = (theme) => css`
  background-color: ${theme.colors.grey};
`

const colCss = css`
  display: flex;
  justify-content: center;
`

const actionsWrapperCss = css`
  width: 100%;
  margin-top: auto;
`

const campaignWrapperCss = (theme) => css`
  background-color: ${theme.colors.grey};
  display: flex;
  flex-wrap: wrap;
  position: relative;
  width: 100%;
  margin-bottom: 32px;
  padding: 24px 16px;

  ${mediaQueries.smallScreen()} {
    margin-bottom: 60px;
    padding: 48px 0 48px 32px;
  }
  ${mediaQueries.mediumScreen()} {
    padding: 48px 32px;
  }
  ${mediaQueries.largeScreen()} {
    min-height: 440px;
    padding: 48px 36px;
  }
  ${mediaQueries.xlargeScreen()} {
    flex-wrap: nowrap;
    padding: 50px 60px;
  }
`

const campaignInfo = css`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueries.smallScreen()} {
    width: 53%;
    padding-right: 24px;
  }
  ${mediaQueries.mediumScreen()} {
    justify-content: space-between;
    padding-right: 20px;
    width: 46%;
  }
  ${mediaQueries.largeScreen()} {
    padding-right: 40px;
  }
  ${mediaQueries.xlargeScreen()} {
    padding-right: 60px;
  }
`

const campaignDescriptionCss = css`
  h4 {
    margin-top: 16px;
    font-size: 28px;
    line-height: 36px;

    ${mediaQueries.xsmallScreen(200)} {
      margin-top: 32px;
      font-size: 36px;
      line-height: 40px;
    }

    ${mediaQueries.smallScreen()} {
      margin-top: 0;
    }
  }
`

const FirstDeal = ({ deal, showMobile, className }) => {
  const isMobile =
    useMedia({ maxWidth: mediaQueries.BASE_SMALL_SIZE - 1 }) || showMobile

  const { name, description, targetUrl, localImage, localLogo, logo, images } =
    deal

  const campaign = {
    id: 'saas-deals-banner',
    image: localImage,
    logo: localLogo,
    applications: [{ name }],
    properties: {
      logo,
      image1x: images?._1x,
      image2x: images?._2x,
      image3x: images?._3x,
    },
  }

  return (
    <article css={campaignWrapperCss} className={className}>
      <div css={campaignInfo} className="saas-deals-campaign-info">
        <AppName campaign={campaign} promoLabel="Premium Partner" />

        {isMobile && (
          <AppImage
            campaign={campaign}
            isMainCampaign
            isMobile
            alt="15% off promo code - livechat15"
          />
        )}
        <div css={campaignDescriptionCss}>
          <h1>{description}</h1>
        </div>

        <div css={actionsWrapperCss}>
          <a href={targetUrl} target="_blank" rel="noopener noreferrer">
            <Button size="large" kind="primary">
              Get it now
            </Button>
          </a>
        </div>
      </div>

      {!isMobile && (
        <AppImage
          campaign={campaign}
          isMainCampaign
          alt="15% off promo code - livechat15"
        />
      )}
    </article>
  )
}

const SaasDeals = ({ data, showMobile, className }) => {
  const [firstDeal, ...deals] = data.allDeal.nodes

  const restOfDeals = deals.reduce((all, one, index) => {
    const chunk = Math.floor(index / numberOfDealsInRow)
    const rowId = `row-${chunk}`

    if (!all[chunk]) {
      all[chunk] = { rowId, deals: [] }
    }

    all[chunk].deals = [...all[chunk].deals, one]
    return all
  }, [])

  return (
    <Layout headerLabel="SaaS Deals">
      <Container>
        <FirstDeal
          deal={firstDeal}
          showMobile={showMobile}
          className={className}
        />

        {restOfDeals.map(({ rowId, deals }) => (
          <Row key={rowId}>
            {deals.map(
              ({ id, name, description, localLogo, logo, targetUrl }) => (
                <Col xs="12" m="4" key={`col-${id}`} css={colCss}>
                  <Card
                    withMargin
                    logo={
                      <LocalImage
                        image={localLogo}
                        fallbackUrl={logo}
                        alt={name}
                      />
                    }
                    css={cardCss}
                    description={
                      <AppDescription campaign={{ description, name }} />
                    }
                    actions={
                      <div css={actionsWrapperCss}>
                        <a
                          href={targetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="large" kind="primary">
                            Get it now
                          </Button>
                        </a>
                      </div>
                    }
                  />
                </Col>
              )
            )}
          </Row>
        ))}
      </Container>
    </Layout>
  )
}

export default SaasDeals

export const Head = () => (
  <SEO
    title="Business Saas Deals &amp; Tools"
    description="LiveChat helps startups become more successful. Our partners give you the best deals for B2B apps and services to get your business going."
    path="/saas-deals/"
  />
)

FirstDeal.propTypes = {
  deal: shape({
    name: string,
    description: string,
    targetUrl: string,
  }),
  showMobile: bool,
  className: string,
}

SaasDeals.propTypes = {
  data: shape({ allDeal: shape({ nodes: array }) }),
  showMobile: bool,
  className: string,
}

export const query = graphql`
  query {
    allDeal {
      nodes {
        id
        name
        targetUrl
        description
        logo
        images {
          _1x
          _2x
          _3x
        }
        localImage {
          childImageSharp {
            gatsbyImageData(quality: 60, placeholder: NONE)
          }
        }
        localLogo {
          childImageSharp {
            gatsbyImageData(height: 40, quality: 40, placeholder: NONE)
          }
        }
      }
    }
  }
`
